import React, { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Set to true when children manage their own padding/scrolling (e.g., forms with sticky footers) */
  noPadding?: boolean;
}

export function Modal({ isOpen, onClose, title, children, size = 'md', noPadding = false }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 transition-opacity animate-fadeIn"
        onClick={() => { haptic.light(); onClose(); }}
      />

      {/* Modal - Bottom Sheet Style */}
      <div
        className={cn(
          'relative w-full bg-dark rounded-t-3xl shadow-2xl shadow-black/50 animate-slideUp',
          'max-h-[92vh] flex flex-col',
          'border-t border-x border-primary/20'
        )}
      >
        {/* Header - Sticky */}
        <div className="flex-shrink-0 bg-dark rounded-t-3xl border-b border-primary/20 px-5 py-4">
          {/* Drag Handle */}
          <div className="flex justify-center mb-3">
            <div className="w-10 h-1 bg-gray-600 rounded-full" />
          </div>

          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-gold">
              {title}
            </h3>
            <button
              onClick={() => { haptic.light(); onClose(); }}
              className="text-gray-400 hover:text-white text-2xl transition-all active:scale-[0.92] p-2 -m-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-dark-lighter"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content - Scrollable or custom layout */}
        <div className={cn(
          'flex-1 overflow-hidden',
          !noPadding && 'overflow-y-auto px-5 py-4 overscroll-contain'
        )}>
          {children}
        </div>
      </div>
    </div>
  );
}
